import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileSpreadsheet, Download } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import {
  usePersonSubmissions,
  type FormSubmissionEntry,
  type UploadSubmissionEntry,
  type DocumentSubmissionEntry,
  type TodoSubmissionEntry,
} from '../../hooks/onboardingOps';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonList } from '../../components/ui/Skeleton';
import { formatDate } from '../../lib/dateFmt';
import { Section, FormItem, DocumentItem, UploadItem, TodoItem } from './SubmissionItems';

export function OnboardingSubmissionDetail() {
  const { personId = '' } = useParams();
  const q = usePersonSubmissions(personId);

  const back = (
    <Link to="/onboarding/submissions" className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
      <ArrowLeft className="h-4 w-4" /> All submissions
    </Link>
  );

  if (q.isLoading) {
    return (
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-8">
        {back}
        <SkeletonList rows={4} />
      </main>
    );
  }
  if (!q.data) {
    return (
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-8">
        {back}
        <EmptyState icon={<FileSpreadsheet className="h-6 w-6" />} title="Not found" description="This person has no record." />
      </main>
    );
  }

  const { person, items, done, total } = q.data;
  const forms = items.filter((i): i is FormSubmissionEntry => i.kind === 'form');
  const documents = items.filter((i): i is DocumentSubmissionEntry => i.kind === 'document');
  const uploads = items.filter((i): i is UploadSubmissionEntry => i.kind === 'upload');
  const todos = items.filter((i): i is TodoSubmissionEntry => i.kind === 'todo');

  return (
    <main className="mx-auto max-w-3xl space-y-5 px-4 py-8">
      {back}
      <PageHeader
        eyebrowIcon={FileSpreadsheet}
        eyebrow="Submissions"
        title={person.full_name}
        description={[
          person.position ?? 'Role unknown',
          person.contract ?? null,
          person.start_date ? `starts ${formatDate(person.start_date)}` : null,
          `${done}/${total} complete`,
        ].filter(Boolean).join(' · ')}
        trailing={
          <a href={`/api/admin/onboarding/people/${personId}/submissions.xlsx`}>
            <Button variant="ghost" size="sm" iconLeft={<Download className="h-4 w-4" />}>
              Export Excel
            </Button>
          </a>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={<FileSpreadsheet className="h-6 w-6" />} title="Nothing assigned" description="This person has no onboarding tasks yet." />
      ) : (
        <div className="space-y-5">
          {forms.length > 0 ? (
            <Section title="Forms">
              {forms.map((e) => <FormItem key={e.task_id} personId={personId} entry={e} />)}
            </Section>
          ) : null}
          {documents.length > 0 ? (
            <Section title="Documents to sign">
              {documents.map((e) => <DocumentItem key={e.task_id} personId={personId} entry={e} />)}
            </Section>
          ) : null}
          {uploads.length > 0 ? (
            <Section title="Uploads">
              {uploads.map((e) => <UploadItem key={e.task_id} personId={personId} entry={e} />)}
            </Section>
          ) : null}
          {todos.length > 0 ? (
            <Section title="To-dos">
              {todos.map((e) => <TodoItem key={e.task_id} entry={e} />)}
            </Section>
          ) : null}
        </div>
      )}
    </main>
  );
}
